import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { GameName } from '../enums';
import type { BinaryReader } from '../BinaryReader';

/**
 * Reconnect codec. Mirrors RegisterModule.cs (server) + Network.cs / GameMonoBehaviour.cs (Unity) and
 * PROTOCOL.md §3.8/§3.9. After an unexpected drop the client re-opens the socket and sends Reconnect(76)
 * with the GUID it received in the Login(2) success reply (gameState.reconnectToken). The server validates
 * the token, re-registers the player WITHOUT a password, and replies on Reconnect(76) telling the client
 * whether it has a live game to resume.
 *
 * Every server branch replies with this same tagged union. The reconnect controller distinguishes terminal
 * token/identity failures from the short, retryable "previous connection is still releasing" window.
 */

/** C2S Reconnect(76): the reconnect token (the GUID from the Login success reply). */
export function encodeReconnect(token: string): Uint8Array {
  return beginMessage(MessageTag.Reconnect).putString(token).toUint8Array();
}

export interface ReconnectResult {
  /** Whether the server accepted the token and re-registered the player. */
  success: boolean;
  /** True when the player had a live game (meaningful only when success). */
  playingGame: boolean;
  /** The live game's type — present only when success && playingGame. */
  gameName?: GameName;
  /** The live game's id, fed back via GetGame(28) to resume — present only when success && playingGame. */
  gameUniqueId?: string;
  /** Persian failure reason — present only when success === false. */
  errorMessage?: string;
}

/**
 * S2C Reconnect(76) — a tagged union keyed on `success`:
 *  - SUCCESS + inGame:  [bool true][bool true][short gameName][string gameUniqueId]
 *  - SUCCESS + noGame:  [bool true][bool false]
 *  - FAILURE:           [bool false][string errorMessage]
 */
export function decodeReconnect(r: BinaryReader): ReconnectResult {
  const success = r.getBool();
  if (!success) {
    // Defensive: some failure branches transmit no message body.
    return { success: false, playingGame: false, errorMessage: r.remaining > 0 ? r.getString() : undefined };
  }
  const playingGame = r.getBool();
  if (!playingGame) {
    return { success: true, playingGame: false };
  }
  return {
    success: true,
    playingGame: true,
    gameName: r.getShort() as GameName,
    gameUniqueId: r.getString(),
  };
}
